import React from 'react';
import useTransactionHistory from '../../hooks/useTransactionHistory';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const CHART_COLORS = {
  red: '#EF4444',
  green: '#10B981',
  blue: '#3B82F6',
  yellow: '#F59E0B',
  purple: '#8B5CF6',
  indigo: '#6366F1',
};

const AdminTransactionHistory = () => {
  const { transactions, loading, pagination, error, fetchTransactions } = useTransactionHistory('admin');
  const [filters, setFilters] = React.useState({});
  const [analytics, setAnalytics] = React.useState(null);
  const [analyticsLoading, setAnalyticsLoading] = React.useState(true);
  const [volumeChart, setVolumeChart] = React.useState(null);
  const [typeChart, setTypeChart] = React.useState(null);

  React.useEffect(() => {
    fetchTransactions(1, filters);
    fetchAnalytics();
  }, [filters]);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://mongobyte-hc73.onrender.com/api/v1/admin/transactions/analytics?period=30d&groupBy=day', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      if (data.success) {
        setAnalytics(data.data);
        
        // Prepare volume chart data using dailyVolume
        const volumeData = {
          labels: data.data.dailyVolume?.map(day => {
            const date = new Date(Date.UTC(day._id.year, day._id.month - 1, day._id.day));
            return date.toLocaleDateString();
          }) || [],
          datasets: [
            {
              label: 'Total Amount',
              data: data.data.dailyVolume?.map(day => day.totalAmount) || [],
              borderColor: CHART_COLORS.blue,
              backgroundColor: '#93C5FD',
              tension: 0.4
            },
            {
              label: 'Credits',
              data: data.data.dailyVolume?.map(day => day.creditAmount) || [],
              borderColor: CHART_COLORS.green,
              backgroundColor: '#86EFAC',
              tension: 0.4
            },
            {
              label: 'Debits',
              data: data.data.dailyVolume?.map(day => day.debitAmount) || [],
              borderColor: CHART_COLORS.red,
              backgroundColor: '#FCA5A5',
              tension: 0.4
            }
          ]
        };
        setVolumeChart(volumeData);

        // Prepare transaction type chart data
        const typeData = {
          labels: data.data.revenueByType?.map(item => item._id.replace(/_/g, ' ')) || [],
          datasets: [
            {
              data: data.data.revenueByType?.map(item => item.totalRevenue) || [],
              backgroundColor: Object.values(CHART_COLORS),
            }
          ]
        };
        setTypeChart(typeData);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleRefund = async (transactionId) => {
    if (!window.confirm('Are you sure you want to process this refund?')) {
      return;
    }

    try {
      const reason = prompt('Please enter the refund reason:');
      if (!reason) return;

      const token = localStorage.getItem('token');
      const response = await fetch(`https://mongobyte-hc73.onrender.com/api/v1/admin/transactions/${transactionId}/refund`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason,
          notifyUser: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to process refund');
      }

      const data = await response.json();
      if (data.success) {
        alert('Refund processed successfully');
        fetchTransactions(pagination.page, filters);
      }
    } catch (error) {
      alert('Error processing refund: ' + error.message);
    }
  };

  const formatAmount = (amount, direction) => {
    const sign = direction === 'credit' ? '+' : '-';
    const color = direction === 'credit' ? 'text-green-600' : 'text-red-600';
    return (
      <span className={color}>
        {sign}₦{amount.toLocaleString()}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6 pt-16 sm:pt-20 pb-16 sm:pb-24">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Transaction Management</h1>

      {/* Analytics Summary */}
      {!analyticsLoading && analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Total Amount</h3>
            <p className="text-lg sm:text-2xl font-bold text-gray-900">
              ₦{(transactions?.reduce((sum, t) => sum + t.amount, 0) || 0).toLocaleString()}
            </p>
            <div className="flex justify-between mt-2">
              <span className="text-sm text-green-600">
                Credit: ₦{(transactions?.filter(t => t.direction === 'credit').reduce((sum, t) => sum + t.amount, 0) || 0).toLocaleString()}
              </span>
              <span className="text-sm text-red-600">
                Debit: ₦{(transactions?.filter(t => t.direction === 'debit').reduce((sum, t) => sum + t.amount, 0) || 0).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Total Transactions</h3>
            <p className="text-2xl font-bold text-gray-900">
              {(transactions?.length || 0).toLocaleString()}
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Average Value</h3>
            <p className="text-2xl font-bold text-gray-900">
              ₦{(transactions?.length ? 
                  (transactions.reduce((sum, t) => sum + t.amount, 0) / transactions.length)
                  .toFixed(2) : '0').toLocaleString()}
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Top Transaction Type</h3>
            <p className="text-2xl font-bold text-gray-900">
              {transactions?.length > 0 ? 
                Object.entries(transactions.reduce((acc, t) => {
                  acc[t.type] = (acc[t.type] || 0) + t.amount;
                  return acc;
                }, {})).sort((a, b) => b[1] - a[1])[0][0].replace(/_/g, ' ') : 
                'No transactions'}
            </p>
          </div>
        </div>
      )}

      {/* Charts */}
      {volumeChart && typeChart && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Daily Transaction Volume</h3>
            <div className="h-64">
              <Line 
                data={volumeChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: value => `₦${value.toLocaleString()}`
                      }
                    },
                    x: {
                      ticks: {
                        maxRotation: 45,
                        minRotation: 45
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top'
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return `${context.dataset.label}: ₦${context.raw.toLocaleString()}`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Revenue by Type</h3>
            <div className="h-64">
              <Pie 
                data={typeChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right'
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border mb-4 sm:mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4">
          <select 
            value={filters.type || ''} 
            onChange={(e) => setFilters(prev => {
              const newFilters = { ...prev };
              if (e.target.value) {
                newFilters.type = e.target.value;
              } else {
                delete newFilters.type;
              }
              return newFilters;
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Types</option>
            <option value="order_payment">Order Payment</option>
            <option value="order_refund">Refund</option>
            <option value="scheduled_order_fee">Scheduled Order Fee</option>
            <option value="commission">Commission</option>
            <option value="penalty_charge">Penalty</option>
          </select>

          <select 
            value={filters.direction || ''} 
            onChange={(e) => setFilters(prev => {
              const newFilters = { ...prev };
              if (e.target.value) {
                newFilters.direction = e.target.value;
              } else {
                delete newFilters.direction;
              }
              return newFilters;
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Directions</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>

          <select 
            value={filters.entityType || ''} 
            onChange={(e) => setFilters(prev => {
              const newFilters = { ...prev };
              if (e.target.value) {
                newFilters.entityType = e.target.value;
              } else {
                delete newFilters.entityType;
              }
              return newFilters;
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">All Entities</option>
            <option value="user">Users</option>
            <option value="restaurant">Restaurants</option>
            <option value="admin">Admin</option>
          </select>

          <div className="relative">
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters(prev => {
                const newFilters = { ...prev };
                if (e.target.value) {
                  newFilters.startDate = e.target.value;
                } else {
                  delete newFilters.startDate;
                }
                return newFilters;
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white placeholder:text-gray-500"
              placeholder="Start Date"
            />
            {!filters.startDate && (
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-500 text-sm">
                Start Date
              </div>
            )}
          </div>

          <div className="relative">
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters(prev => {
                const newFilters = { ...prev };
                if (e.target.value) {
                  newFilters.endDate = e.target.value;
                } else {
                  delete newFilters.endDate;
                }
                return newFilters;
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white placeholder:text-gray-500"
              placeholder="End Date"
            />
            {!filters.endDate && (
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-500 text-sm">
                End Date
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <input
            type="text"
            value={filters.search || ''}
            onChange={(e) => setFilters(prev => {
              const newFilters = { ...prev };
              if (e.target.value) {
                newFilters.search = e.target.value;
              } else {
                delete newFilters.search;
              }
              return newFilters;
            })}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white placeholder:text-gray-500"
            placeholder="Search in descriptions..."
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error: {error}</p>
          <button 
            onClick={() => fetchTransactions(1, filters)}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Transaction List */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-sm border">
          {transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No transactions found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="hidden sm:block">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                      <tr key={transaction._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{transaction.transactionId}</p>
                            <p className="text-xs text-gray-500">{new Date(transaction.createdAt).toLocaleString()}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm text-gray-900">
                              {transaction.relatedUser?.username || transaction.relatedRestaurant?.name || 'System'}
                            </p>
                            <p className="text-xs text-gray-500">{transaction.entityType}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">
                            {transaction.type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            {formatAmount(transaction.amount, transaction.direction)}
                            <p className="text-xs text-gray-500">
                              Balance: ₦{transaction.balanceAfter.toLocaleString()}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            transaction.status === 'completed' 
                              ? 'bg-green-100 text-green-800'
                              : transaction.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {transaction.type === 'order_payment' && (
                            <button
                              onClick={() => handleRefund(transaction.transactionId)}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Process Refund
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Mobile view */}
              <div className="sm:hidden">
                {transactions.map((transaction) => (
                  <div key={transaction._id} className="bg-white border-b p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{transaction.transactionId}</p>
                          <p className="text-xs text-gray-500">{new Date(transaction.createdAt).toLocaleString()}</p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : transaction.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-900">
                          {transaction.relatedUser?.username || transaction.relatedRestaurant?.name || 'System'}
                          <p className="text-xs text-gray-500">{transaction.entityType}</p>
                        </div>
                        <div className="text-right">
                          {formatAmount(transaction.amount, transaction.direction)}
                          <p className="text-xs text-gray-500">
                            Balance: ₦{transaction.balanceAfter.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          {transaction.type.replace(/_/g, ' ')}
                        </span>
                        {transaction.type === 'order_payment' && (
                          <button
                            onClick={() => handleRefund(transaction.transactionId)}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Process Refund
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-3 sm:px-4 py-3 border-t bg-gray-50 flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => fetchTransactions(pagination.page - 1, filters)}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md">
                  {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => fetchTransactions(pagination.page + 1, filters)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminTransactionHistory;